import React, { useState, useEffect, useCallback } from 'react';
import { addSyncListener, syncAll, getPendingSyncItemsDetailed } from '../services/SyncManager';
import { RefreshCw, CheckCircle, XCircle, Clock, X, Wifi, WifiOff, MapPin, ShoppingBag, Receipt } from 'lucide-react';
import { Button } from './ui/button';

const typeIcons = {
    visit: MapPin,
    order: ShoppingBag,
    expense: Receipt
};

const typeLabels = {
    visit: 'زيارة',
    order: 'طلب',
    expense: 'مصروف'
};

const statusConfig = {
    pending: { icon: Clock, label: 'في الانتظار', color: 'text-slate-400', bg: 'bg-slate-50' },
    syncing: { icon: RefreshCw, label: 'جاري المزامنة...', color: 'text-blue-500', bg: 'bg-blue-50', animate: true },
    done: { icon: CheckCircle, label: 'تمت المزامنة', color: 'text-green-500', bg: 'bg-green-50' },
    failed: { icon: XCircle, label: 'فشل', color: 'text-red-500', bg: 'bg-red-50' }
};

const SyncDetailPanel = ({ isOpen, onClose }) => {
    const [items, setItems] = useState([]);
    const [syncState, setSyncState] = useState('idle'); // idle, syncing, complete
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [results, setResults] = useState(null);

    // Load pending items on open
    useEffect(() => {
        if (isOpen) {
            loadPendingItems();
        }
    }, [isOpen]);

    const loadPendingItems = async () => {
        try {
            const pending = await getPendingSyncItemsDetailed();
            setItems(pending);
        } catch (e) {
            console.error('Failed to load pending items:', e);
        }
    };

    // Listen for sync events
    useEffect(() => {
        const unsubscribe = addSyncListener((event) => {
            switch (event.type) {
                case 'sync_start':
                    setSyncState('syncing');
                    setProgress({ current: 0, total: event.total || 0 });
                    if (event.items) setItems(event.items);
                    break;
                case 'sync_progress':
                    setProgress({ current: event.current, total: event.total });
                    if (event.items) setItems([...event.items]);
                    break;
                case 'sync_complete':
                    setSyncState('complete');
                    setResults(event.results);
                    if (event.items) setItems([...event.items]);
                    break;
                case 'sync_error':
                    setSyncState('idle');
                    break;
            }
        });
        return unsubscribe;
    }, []);

    const handleSync = useCallback(async () => {
        setSyncState('syncing');
        await syncAll();
    }, []);

    if (!isOpen) return null;

    const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${syncState === 'syncing' ? 'bg-blue-100' : syncState === 'complete' ? 'bg-green-100' : 'bg-slate-100'}`}>
                            <RefreshCw className={`h-5 w-5 ${syncState === 'syncing' ? 'text-blue-600 animate-spin' : syncState === 'complete' ? 'text-green-600' : 'text-slate-600'}`} />
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 dark:text-white">تفاصيل المزامنة</h3>
                            <p className="text-xs text-slate-500">
                                {syncState === 'syncing' ? `جاري المزامنة ${progress.current}/${progress.total}` :
                                    syncState === 'complete' ? `تمت المزامنة ✅` :
                                        `${items.length} عنصر بانتظار المزامنة`}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
                        <X className="h-5 w-5 text-slate-400" />
                    </button>
                </div>

                {/* Progress bar */}
                {syncState === 'syncing' && (
                    <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20">
                        <div className="flex items-center justify-between text-xs text-blue-600 mb-1">
                            <span>تقدم المزامنة</span>
                            <span>{progressPercent}%</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progressPercent}%` }} />
                        </div>
                    </div>
                )}

                {/* Results Summary */}
                {syncState === 'complete' && results && (
                    <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            {results.synced} ناجح
                        </span>
                        {results.failed > 0 && (
                            <span className="flex items-center gap-1 text-red-500">
                                <XCircle className="h-4 w-4" />
                                {results.failed} فشل
                            </span>
                        )}
                    </div>
                )}

                {/* Items List */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {items.length === 0 ? (
                        <div className="py-12 text-center">
                            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                            <p className="text-slate-600 font-medium">لا توجد عناصر بانتظار المزامنة</p>
                            <p className="text-sm text-slate-400 mt-1">جميع البيانات محدثة ✓</p>
                        </div>
                    ) : (
                        items.map((item, idx) => {
                            const TypeIcon = typeIcons[item.type] || Clock;
                            const status = statusConfig[item.status] || statusConfig.pending;
                            const StatusIcon = status.icon;

                            return (
                                <div key={item.id || idx} className={`flex items-center gap-3 p-3 rounded-xl ${status.bg} transition-all duration-200`}>
                                    <div className={`p-2 rounded-lg bg-white dark:bg-slate-800 shadow-sm`}>
                                        <TypeIcon className={`h-4 w-4 ${status.color}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-slate-900 dark:text-white text-sm truncate">
                                                {item.description || typeLabels[item.type] || item.type}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                                                {typeLabels[item.type] || item.type}
                                            </span>
                                        </div>
                                        {item.clinic_name && (
                                            <p className="text-xs text-slate-500 truncate">🏥 {item.clinic_name}</p>
                                        )}
                                        {item.error && (
                                            <p className="text-xs text-red-500 mt-0.5">❌ {item.error}</p>
                                        )}
                                    </div>
                                    <StatusIcon className={`h-5 w-5 ${status.color} ${status.animate ? 'animate-spin' : ''} flex-shrink-0`} />
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                    <Button
                        onClick={handleSync}
                        disabled={syncState === 'syncing'}
                        className="flex-1 bg-primary hover:bg-primary/90"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${syncState === 'syncing' ? 'animate-spin' : ''}`} />
                        {syncState === 'syncing' ? 'جاري المزامنة...' : 'مزامنة الآن'}
                    </Button>
                    <Button variant="outline" onClick={onClose}>
                        إغلاق
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SyncDetailPanel;
